/**
 * This file has the message formats for the different messages that are
 * received by the client from the websocket.
 *
 * Each websocket `message` contains an `event` that has a `type` and `data`.
 * These `events` are then reused by the `store` and `actions`.
 */

import { OnlineOfflineStatusType } from '../online-offline/online-offline.model';

export type WebsocketServerEvent = SubMessageEvent | WebsocketMessage;

interface SubMessageEvent {
  channel: 'subscribe';
  body: 'OK';
}

interface WebsocketProjectData {
  address?: string;
  appended_descr: string;
  currency?: string;
  currencyCode: string;
  exchangerate: string;
  featured: boolean;
  free_bid_until?: number;
  fulltime: boolean;
  hideBids: boolean;
  id: number;
  imgUrl: string;
  isLocal: boolean;
  isMinimallyPostedProject: boolean;
  isTest: boolean;
  jobs: string[];
  jobString: string;
  linkUrl: string;
  maxbudget?: number;
  minbudget?: number;
  minimallyPostedProjectBudgetSet: boolean;
  nonpublic: boolean;
  projIsHourly: boolean;
  publicName?: string;
  showBudgetMinimallyPostedProject: boolean;
  submitDate: string;
  text: string;
  time: number;
  title: string;
  userId: number;
  userName: string;
}

interface BaseServerData {
  data: { id: string | number; time: number };
}

export interface WebsocketAwardEvent extends BaseServerData {
  type: 'award';
  data: {
    acceptByTime?: number;
    actionText?: string;
    apiMessage?: {
      bid: {
        amount: number;
        award_status: 'pending';
        bidder_id: number;
        id: number;
        period: number;
      };
      project: {
        currency: { code: string; sign: string };
        id: number;
        owner_id: number;
        title: string;
        type: 'fixed';
      };
      users: { [id: string]: { username: string } };
    };
    appended_descr?: string;
    awardeeId?: number;
    awardeeName?: string;
    bid?: {
      bidAmount: string;
      bidDescr: null;
      bidId: number;
      bidPeriod: string;
      id: number;
      submitTime: number;
    };
    categoryName?: string;
    currencycode?: string;
    currencysign?: string;
    id: number;
    imgUrl?: string;
    isTest?: boolean;
    jobs: string[];
    jobString: string;
    linkUrl?: string;
    projIsHourly?: boolean;
    text?: string;
    time: number;
    title: string;
    userId: number;
    userName: string;
  };
}

export interface WebsocketAwardBadgeEvent extends BaseServerData {
  type: 'awardBadge';
  data: {
    descr: string;
    id: number;
    name: string;
    time: number;
  };
}

export interface WebsocketAwardCreditEvent extends BaseServerData {
  type: 'awardCredit';
  data: {
    amount: string; // I think?
    id: string | number;
    time: number;
  };
}

export interface WebsocketAwardReminderEvent extends BaseServerData {
  type: 'awardReminder';
  data: {
    id: string | number;
    name: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketAwardMilestoneReminderEvent extends BaseServerData {
  type: 'awardMilestoneReminder';
  data: {
    id: string | number;
    publicName?: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketAwardXpEvent extends BaseServerData {
  type: 'awardXp';
  data: {
    amount: string; // I think?
    descr: string;
    id: string | number;
    time: number;
  };
}

interface WebsocketBidEventProjectCount {
  all: number;
  incomplete_reviews: number;
  complete: number;
  completion_rate: number;
  reviews: number;
  earnings: number;
  incomplete: number;
}

interface WebsocketBidEventReputation {
  com: number;
  exp: number;
  hag: number;
  onb: number;
  ont: number;
  overall: number;
  pos: number;
  pro: number;
  qua: number;
}

interface WebsocketBidEventSellerRating {
  count: number;
  projectCount: {
    last12months: WebsocketBidEventProjectCount;
    last3months: WebsocketBidEventProjectCount;
    entire_history: WebsocketBidEventProjectCount;
  };
  completionRate: number;
  avg: number;
  reputation: {
    last12months: WebsocketBidEventReputation;
    last3months: WebsocketBidEventReputation;
    entire_history: WebsocketBidEventReputation;
  };
}
export interface WebsocketBidEvent extends BaseServerData {
  type: 'bid';
  data: {
    accountId?: number;
    amount: string;
    award?: string;
    bidAvg?: string;
    bidCount?: number;
    bidHidden: boolean;
    bidNo?: string;
    buyerFeePerDollar?: string;
    buyerMinProjectFee?: string;
    categoryName?: string;
    currency: string;
    currencyCode: string;
    currencyId: string;
    descr: string;
    id: number;
    imgUrl: string;
    initMilestone?: number;
    linkUrl?: string;
    period: string;
    pmb: undefined;
    projectId: string;
    projIsFullTime: boolean;
    projIsHourly: boolean;
    projName: string;
    publicName?: string;
    showAwardFee: boolean;
    text?: string;
    time: number;
    title: string;
    userId: string;
    userName: string;
    userBalances?: {
      all: {
        currency: {
          code: string;
          country: string;
          exchangerate: string;
          id: string;
          is_external?: string;
          name: string;
          seq: string;
          sign: string;
        };
        balance: string;
      }[];
      relevant: number;
    };
    bid: {
      award_score?: number;
      completed?: boolean;
      completion_score?: number;
      descr_html?: string;
      descr_short_html?: string;
      descr_short?: string;
      descr?: null;
      expert_guarantee?: null;
      highlighted?: boolean;
      id?: number;
      isShortlisted?: null;
      milestone_percentage?: string;
      new_ranking?: number;
      no?: string;
      paid_status?: null;
      period?: string;
      project_id?: string;
      r2?: number;
      ranking?: number;
      score?: number;
      sponsored?: null;
      submitdate_f?: string;
      submitdate_f2?: ' recently';
      submitdate_ts?: number;
      submitdate?: string;
      sum?: string;
      users_id?: number;
      user?: {
        address: { country: string; code: string; city: null };
        company: string;
        currency: number;
        earnings: number;
        flag_class: string;
        flag_name: string;
        flag: { name: string; icon: string };
        gold: number;
        hourlyrate: number;
        id: number;
        insignia: {
          name: string;
          title: string;
          examEnabled: string;
          examId: string;
          id: string;
          description: string;
        }[];
        isPortfolioCreated: boolean;
        jobs: string[];
        logo_Url: string;
        normalMemberclassLogo: string;
        online?: string;
        profile_logo_Url: string;
        provider_rating: { count: number; avg: string };
        publicName?: string;
        rating: { count: number; avg: string };
        reg_date: string;
        reg_unixtime: number;
        reputation_rate: number;
        timezone: number;
        tooltipMemberclassLogo: string;
        Url: string;
        username: string;
        seller_rating: WebsocketBidEventSellerRating;
      };
    };
  };
}

export interface WebsocketCompletedEvent extends BaseServerData {
  type: 'completed';
  data: {
    id: string | number;
    name: string;
    state?: 'completed'; // I think
    time: number;
    userName: string;
  };
}

export interface WebsocketContactRequestReceivedEvent extends BaseServerData {
  type: 'contactRequestReceived';
  data: {
    id: string | number;
    pendingAjax: boolean;
    request: { state: 'accepted' | 'pending' | 'rejected' };
    time: number;
    user: { username: string };
  };
}

export interface WebsocketContestEvent extends BaseServerData {
  type: 'contest';
  data: {
    buyer?: string;
    buyerUrl?: string;
    currency?: string;
    currencyCode: string;
    exchangerate: string;
    extended: boolean;
    featured: boolean;
    free_bid_until?: number;
    highlight: undefined;
    id: string | number;
    imgUrl: string;
    isTest: boolean;
    jobs: number[];
    jobString: string;
    linkUrl: string;
    locked: boolean;
    maxbudget?: string; // I think?
    private: boolean;
    sealed: boolean;
    submitDate: string;
    time: number;
    title: string;
    userId: string;
    userName: string;
  };
}

export interface WebsocketContestAwardedToEmployerEvent extends BaseServerData {
  type: 'contestAwardedToEmployer';
  data: {
    contestName: string;
    contestSEOUrl: string;
    currencyCode: string;
    currencySign: string;
    entry_id: string;
    entry_number: string;
    entryCount: string;
    freelancerCount: string;
    id: string | number;
    imgUrl: string;
    isBought: boolean;
    linkUrl: string;
    prize: string;
    publicName?: string;
    thumb: string;
    time: number;
    userId: number;
    userName: string;
  };
}

export interface WebsocketContestAwardedToFreelancerEvent
  extends BaseServerData {
  type: 'contestAwardedToFreelancer';
  data: {
    contestName: string;
    entry_number: string; // I think?
    id: string | number;
    time: number;
  };
}

export interface WebsocketContestEntryBoughtToEmployerEvent
  extends BaseServerData {
  type: 'contestEntryBoughtToEmployer';
  data: {
    contestName: string;
    entry_number: string; // I think?
    id: string | number;
    publicName?: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketContestEntryBoughtToFreelancerEvent
  extends BaseServerData {
  type: 'contestEntryBoughtToFreelancer';
  data: {
    contestName: string;
    entry_number: string; // I think?
    id: string | number;
    time: number;
  };
}

export interface WebsocketContestEntryEvent extends BaseServerData {
  type: 'contestEntry';
  data: {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: number;
  };
}

export interface WebsocketContestPCBNotificationEvent extends BaseServerData {
  type: 'contestPCBNotification';
  data: {
    contestHolder: string;
    contestId: string;
    contestName: string;
    firstUser?: string;
    firstUserPublicName?: string;
    id: string | number;
    imgUrl: string;
    linkUrl: string;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
    userId: number;
  };
}

export interface WebsocketContestPCBNotificationFullViewEvent
  extends BaseServerData {
  type: 'contestPCBNotificationFullView';
  data: {
    contestName: string;
    entryNumber: string; // I think?
    firstUser: string;
    firstUserPublicName?: string;
    id: string | number;
    secondUser?: string;
    secondUserPublicName?: string;
    time: number;
  };
}

export interface WebsocketCreateMilestoneEvent extends BaseServerData {
  type: 'createMilestone';
  data: {
    amount: string; // I think?
    currencycode: string;
    currencysign: string;
    id: string | number;
    publicName?: string;
    time: number;
    title: string;
    userName: string;
  };
}

export interface WebsocketDenyedEvent extends BaseServerData {
  type: 'denyed';
  data: {
    id: string | number;
    name: string;
    publicName?: string;
    time: number;
    userName: string;
  };
}

export interface WebsocketDraftContestEvent extends BaseServerData {
  type: 'draftContest';
  data: {
    contestEditUrl?: string;
    contestId: string;
    contestName: string;
    contestUrl: string;
    id: string | number;
    imgUrl: string;
    isPublished: boolean;
    time: number;
  };
}

export interface WebsocketFailingProjectEvent extends BaseServerData {
  type: 'failingProject';
  data: WebsocketProjectData & {
    id: string | number;
    projectFreeBid: boolean;
    time: number;
    xpValue: number;
  };
}

export interface WebsocketHighLtvAnnualDiscountEvent extends BaseServerData {
  type: 'highLtvAnnualDiscount';
  data: {
    id: string | number;
    time: number;
    userDisplayName: string;
  };
}

export interface WebsocketInviteUserBidEvent extends BaseServerData {
  type: 'inviteUserBid';
  data: {
    id: string | number;
    jobString: string;
    publicName?: string;
    time: number;
    title: string;
    userName: string;
  };
}

export interface WebsocketInviteToContestEvent extends BaseServerData {
  type: 'inviteToContest';
  data: {
    buyerName: string;
    buyerPublicName?: string;
    currencycode: string;
    currencysign: string;
    id: string | number;
    prize: string; // I think?
    time: number;
    title: string;
  };
}

export interface WebsocketInvoiceRequestedEvent extends BaseServerData {
  type: 'invoiceRequested';
  data: {
    amount: string; // I think?
    currencyCode: string;
    currencySign: string;
    id: string | number;
    name: string;
    publicName?: string;
    state?: 'paid'; // I think?
    time: number;
    userName: string;
  };
}

export interface WebsocketLevelupEvent extends BaseServerData {
  type: 'levelup';
  data: {
    id: string | number;
    level: string; // I think?
    membership: boolean;
    perks: {
      description: string;
      item: boolean;
      magnitude: number;
      name?: 'BidRefreshRate' | 'item';
      symbol: string;
    }[];
    time: number;
    username: string;
  };
}

export interface WebsocketNotifyfollowerEvent extends BaseServerData {
  type: 'notifyfollower';
  data: {
    id: string | number;
    jobString: string;
    time: number;
    title: string;
    userName: string;
  };
}

export interface WebsocketProjectRecruiterEvent extends BaseServerData {
  type: 'projectRecruiter';
  data: {
    agent: { username: string };
    id: string | number;
    message: string;
    project: { name: string };
    time: number;
  };
}

export interface WebsocketProjectEvent extends BaseServerData {
  type: 'project';
  data: WebsocketProjectData;
}

export interface WebsocketQuickHireProjectEvent extends BaseServerData {
  type: 'quickHireProject';
  data: {
    currency?: { code: string; sign: string };
    id: string | number;
    jobString: string;
    period?: number;
    projIsHourly: boolean;
    sum?: string; // I think?
    time: number;
    title: string;
    userName: string;
  };
}

export interface WebsocketReleaseMilestoneEvent extends BaseServerData {
  type: 'releaseMilestone';
  data: {
    amount: string; // I think?
    currencycode: string;
    currencysign: string;
    id: string | number;
    publicName?: string;
    time: number;
    title: string;
    userName: string;
  };
}

export interface WebsocketRequestEndProjectEvent extends BaseServerData {
  type: 'requestEndProject';
  data: {
    dayCount?: string; // I think?
    id: string | number;
    publicName?: string;
    time: number;
    name: string;
    userName: string;
  };
}

export interface WebsocketRequestMilestoneEvent extends BaseServerData {
  type: 'requestMilestone';
  data: {
    amount: string; // I think?
    currencyCode: string;
    currencySign: string;
    id: string | number;
    name: string;
    publicName?: string;
    state?: 'accept'; // I think?
    time: number;
    userName: string;
  };
}

export interface WebsocketRequestToReleaseEvent extends BaseServerData {
  type: 'requestToRelease';
  data: {
    amount: string; // I think?
    currencycode: string;
    currencysign: string;
    id: string | number;
    name: string;
    publicName?: string;
    state?: 'accept'; // I think?
    time: number;
    userName: string;
  };
}

export interface WebsocketServiceApprovedEvent extends BaseServerData {
  type: 'serviceApproved';
  data: {
    id: string | number;
    service_name: string;
    time: number;
  };
}

export interface WebsocketServiceProjectEndingSellerEvent
  extends BaseServerData {
  type: 'serviceProjectEndingSeller';
  data: {
    buyer_name: string;
    hours_remaining?: number;
    id: string | number;
    order_start_date: string;
    service_name: string;
    time: number;
  };
}

export interface WebsocketServiceProjectPostedSellerEvent
  extends BaseServerData {
  type: 'serviceProjectPostedSeller';
  data: {
    buyer_name: string;
    buyer_public_name?: string;
    id: string | number;
    service_name: string;
    time: number;
  };
}

export interface WebsocketServiceRejectedEvent extends BaseServerData {
  type: 'serviceRejected';
  data: {
    id: string | number;
    service_name: string;
    time: number;
  };
}

export interface WebsocketSignUpFreeTrialUpsellEvent extends BaseServerData {
  type: 'signUpFreeTrialUpsell';
  data: {
    id: string | number;
    time: number;
    trialPackageName: string;
  };
}

export interface WebsocketSitesBinCreatedEvent extends BaseServerData {
  type: 'sitesBinCreated';
  data: {
    bid: { bidder: { username: string } };
    id: string | number;
    listing: { url: string };
    time: number;
  };
}

export interface WebsocketSitesHandOverMessageSentEvent extends BaseServerData {
  type: 'sitesHandOverMessageSent';
  data: {
    id: string | number;
    listing: { url: string };
    sender: { username: string };
    time: number;
  };
}

export interface WebsocketSitesListingStatusChangedEvent
  extends BaseServerData {
  type: 'sitesListingStatusChanged';
  data: {
    id: string | number;
    listing:
      | { status: 'activated'; url: string }
      | { status: 'bin_rejected'; url: string }
      | { status: 'closed_bidder'; url: string }
      | { status: 'closed'; url: string }
      | { status: 'closing_bidder'; url: string }
      | { status: 'closing'; url: string }
      | { status: 'completed_bidder'; url: string }
      | { status: 'completed'; url: string }
      | { status: 'draft'; url: string }
      | { status: 'feedback_reminder_bidder'; url: string }
      | { status: 'feedback_reminder'; url: string }
      | {
          status: 'funds_released_bidder';
          fmt_price: string;
          seller_name: string;
          url: string;
        }
      | {
          status: 'funds_released';
          bidder_name: string;
          fmt_price: string;
          url: string;
        }
      | { status: 'outbid'; url: string }
      | { status: 'reserve_achieved_bidder'; url: string }
      | { status: 'reserve_achieved'; url: string };
    time: number;
  };
}

export interface WebsocketSitesMessageSentEvent extends BaseServerData {
  type: 'sitesMessageSent';
  data: {
    id: string | number;
    listing: { url: string };
    sender: { username: string };
    time: number;
  };
}

export interface WebsocketSitesOfferCreatedEvent extends BaseServerData {
  type: 'sitesOfferCreated';
  data: {
    bid: { bidder: { username: string } };
    id: string | number;
    listing: { url: string };
    time: number;
  };
}

export interface WebsocketUpsellCrowdsourceFindingEvent extends BaseServerData {
  type: 'upsellCrowdsourceFinding';
  data: {
    id: string | number;
    project_name: string;
    time: number;
  };
}

export interface WebsocketXpContestEvent extends BaseServerData {
  type: 'xpContest';
  data: {
    currency?: string;
    id: string | number;
    jobString: string;
    maxbudget?: number;
    time: number;
    title: string;
    userName: string;
  };
}

type WebsocketStandardEvent =
  | WebsocketAwardEvent
  | WebsocketAwardBadgeEvent
  | WebsocketAwardCreditEvent
  | WebsocketAwardMilestoneReminderEvent
  | WebsocketAwardReminderEvent
  | WebsocketAwardXpEvent
  | WebsocketBidEvent
  | WebsocketCompletedEvent
  | WebsocketContactRequestReceivedEvent
  | WebsocketContestEvent
  | WebsocketContestAwardedToEmployerEvent
  | WebsocketContestAwardedToFreelancerEvent
  | WebsocketContestEntryEvent
  | WebsocketContestEntryBoughtToEmployerEvent
  | WebsocketContestEntryBoughtToFreelancerEvent
  | WebsocketContestPCBNotificationEvent
  | WebsocketContestPCBNotificationFullViewEvent
  | WebsocketCreateMilestoneEvent
  | WebsocketDenyedEvent
  | WebsocketDraftContestEvent
  | WebsocketFailingProjectEvent
  | WebsocketHighLtvAnnualDiscountEvent
  | WebsocketInviteToContestEvent
  | WebsocketInviteUserBidEvent
  | WebsocketInvoiceRequestedEvent
  | WebsocketLevelupEvent
  | WebsocketNotifyfollowerEvent
  | WebsocketProjectEvent
  | WebsocketProjectRecruiterEvent
  | WebsocketQuickHireProjectEvent
  | WebsocketReleaseMilestoneEvent
  | WebsocketRequestEndProjectEvent
  | WebsocketRequestMilestoneEvent
  | WebsocketRequestToReleaseEvent
  | WebsocketServiceApprovedEvent
  | WebsocketServiceProjectEndingSellerEvent
  | WebsocketServiceProjectPostedSellerEvent
  | WebsocketServiceRejectedEvent
  | WebsocketSignUpFreeTrialUpsellEvent
  | WebsocketSitesBinCreatedEvent
  | WebsocketSitesHandOverMessageSentEvent
  | WebsocketSitesListingStatusChangedEvent
  | WebsocketSitesMessageSentEvent
  | WebsocketSitesOfferCreatedEvent
  | WebsocketUpsellCrowdsourceFindingEvent
  | WebsocketXpContestEvent;

type BaseMessage = BaseServerData & {
  channel: 'user';
  body: {
    aggregated: null;
    display: boolean;
    id: string;
    is_important: boolean;
    is_notification: boolean;
    is_project: boolean;
    message: string;
    news_feed: boolean;
    no_persist: boolean;
    popup: boolean;
    targets: any[];
    timestamp: number;
  };
};

type WebsocketStandardMessage = BaseMessage & {
  body: WebsocketStandardEvent & { parent_type: 'notifications' };
};

type CustomMessage<Type, Data, ParentType> = BaseMessage & {
  body: {
    type: Type;
    data: Data;
    parent_type: ParentType;
  };
};

interface OnlineOfflineData {
  [userId: string]: OnlineOfflineStatusType;
}

type OnlineOfflineMessage = CustomMessage<
  'statusget',
  OnlineOfflineData,
  'onlineoffline'
>;

export type WebsocketBaseEvent =
  | WebsocketStandardEvent
  | { type: 'onlineOffline'; data: OnlineOfflineData };

export type WebsocketMessage = WebsocketStandardMessage | OnlineOfflineMessage;

export function isOnlineOfflineEvent(
  event: WebsocketMessage,
): event is OnlineOfflineMessage {
  return (
    event.channel === 'user' &&
    event.body &&
    event.body.parent_type === 'onlineoffline'
  );
}

export function isSockMessageServerEvent(
  event: WebsocketServerEvent,
): event is WebsocketMessage {
  return event.channel === 'user';
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build/webapp/src/app/@shared/datastore/websocket/server-message-event.model.ts